import {
  INSPECTION_STATUSES,
  INSPECTION_STATUS_LABELS,
  type InspectionStatus,
} from '@sitewise/shared';
import {
  Button,
  DataTable,
  EmptyState,
  ErrorState,
  Input,
  Skeleton,
  cn,
} from '@sitewise/ui';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, Filter, Plus, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { listInspections, type InspectionSummary } from '../../../api/inspections.js';

import { CreateInspectionSheet } from './create-inspection-sheet.js';
import { formatDate, StatusBadge } from './lib.js';

interface FilterState {
  q: string;
  status: InspectionStatus[];
}

export function InspectionsListPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [filters, setFilters] = useState<FilterState>({
    q: params.get('q') ?? '',
    status: parseList(params.get('status')) as InspectionStatus[],
  });
  const [showCreate, setShowCreate] = useState(false);

  const queryKey = useMemo(() => ['inspections', 'list', filters], [filters]);
  const inspQuery = useQuery({
    queryKey,
    queryFn: () =>
      listInspections({
        q: filters.q || undefined,
        status: filters.status.length > 0 ? filters.status : undefined,
        sort: '-scheduled_for',
      }),
    placeholderData: (prev) => prev,
  });

  function applyFilter(next: FilterState) {
    setFilters(next);
    const sp = new URLSearchParams();
    if (next.q) sp.set('q', next.q);
    if (next.status.length > 0) sp.set('status', next.status.join(','));
    setParams(sp, { replace: true });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted" />
          <Input
            placeholder="Search inspections…"
            value={filters.q}
            onChange={(e) => applyFilter({ ...filters, q: e.target.value })}
            className="w-72 pl-8"
          />
        </div>
        <StatusFilter
          value={filters.status}
          onChange={(s) => applyFilter({ ...filters, status: s })}
        />
        <div className="ml-auto">
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4" />
            New inspection
          </Button>
        </div>
      </div>

      {inspQuery.isError ? (
        <ErrorState description="Could not load inspections." />
      ) : inspQuery.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (inspQuery.data?.inspections.length ?? 0) === 0 ? (
        <EmptyState
          icon={ClipboardList}
          title="No inspections yet"
          description="Run a one-off inspection or add a recurring schedule to start collecting them here."
          action={<Button onClick={() => setShowCreate(true)}>New inspection</Button>}
        />
      ) : (
        <DataTable<InspectionSummary>
          rowKey={(r) => r.id}
          rows={inspQuery.data!.inspections}
          onRowClick={(r) => navigate(`/inspections/runs/${r.id}`)}
          columns={[
            {
              id: 'id',
              header: 'Inspection',
              description: 'The inspection, and whether it covers a unit or the whole building.',
              cell: (r) => (
                <div className="min-w-0">
                  <Link
                    to={`/inspections/runs/${r.id}`}
                    className="block truncate font-medium hover:underline"
                  >
                    {r.id.slice(0, 8)}
                  </Link>
                  <div className="truncate text-xs text-fg-muted">
                    {r.unitId ? 'Unit-level' : 'Building-level'}
                  </div>
                </div>
              ),
            },
            {
              id: 'scheduledFor',
              header: 'Scheduled',
              description: 'When this inspection is scheduled to happen.',
              cell: (r) => (
                <div className="whitespace-nowrap tabular-nums">{formatDate(r.scheduledFor)}</div>
              ),
            },
            {
              id: 'submittedAt',
              header: 'Submitted',
              description: 'When the inspection was turned in.',
              cell: (r) => (
                <div className="whitespace-nowrap tabular-nums text-fg-muted">
                  {formatDate(r.submittedAt)}
                </div>
              ),
            },
            {
              id: 'status',
              header: 'Status',
              description: 'Whether the inspection is scheduled, in progress, or complete.',
              cell: (r) => <StatusBadge status={r.status} />,
            },
          ]}
        />
      )}

      {showCreate ? <CreateInspectionSheet onClose={() => setShowCreate(false)} /> : null}
    </div>
  );
}

function StatusFilter({
  value,
  onChange,
}: {
  value: InspectionStatus[];
  onChange: (next: InspectionStatus[]) => void;
}) {
  function toggle(status: InspectionStatus) {
    const has = value.includes(status);
    onChange(has ? value.filter((s) => s !== status) : [...value, status]);
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Filter className="h-4 w-4 text-fg-muted" />
      {INSPECTION_STATUSES.map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => toggle(s)}
          className={cn(
            'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
            value.includes(s)
              ? 'border-primary bg-primary-subtle text-primary'
              : 'border-border bg-surface text-fg-secondary hover:border-fg-muted',
          )}
        >
          {INSPECTION_STATUS_LABELS[s]}
        </button>
      ))}
    </div>
  );
}

function parseList(value: string | null): string[] {
  if (!value) return [];
  return value
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}
